import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import { Select } from 'ts/interfaces/Generic';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { showError } from 'utils/utils';
import { faCopy, faShuffle } from '@fortawesome/free-solid-svg-icons';
import { Dispatch, FC, MutableRefObject, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { moManualRoomTypeOptions, paxTypeOptions, titlePassengerOptions } from 'constants/constant';
import { Order, Room } from 'ts/shared/interfaces/MOResponse';
import { getRandomName, segmentEditorOther } from 'services/myOrders';
import withErrorBoundary from '../../withErrorBoundary';

interface ManualRoomRow {
  roomType: string;
  childrenAge: string;
  meal: string;
  status: string;
  supplierRoomCode: string;
  basicDesc: string;
  cxlDate: string;
}

const roomCountOptions: Select[] = Array.from({ length: 24 }, (_, idx) => ({
  value: String(idx + 1),
  label: `${idx + 1} ${idx === 0 ? 'Room' : 'Rooms'}`,
}));

const mealOptions: Select[] = [
  { value: 'RO', label: 'RO - Room Only' },
  { value: 'BB', label: 'BB - Breakfast Included' },
  { value: 'HB', label: 'HB - Half Board' },
  { value: 'FB', label: 'FB - Full Board' },
  { value: 'AI', label: 'AI - All Inclusive' },
];
const childAgeOptions: Select[] = Array.from({ length: 15 }, (_, idx) => {
  const age = idx + 2;
  return { value: String(age), label: String(age) };
});
const titleOptions: Select[] = titlePassengerOptions.map((item) => ({
  value: item.value,
  label: item.label.replace('passenger.', '').toUpperCase(),
}));
const childPaxType = paxTypeOptions.find((item) => item.value === 'CNN');
const childTitleOption: Select = {
  value: childPaxType?.value || 'CNN',
  label: childPaxType?.label || 'Child',
};

const normalizeTitleValue = (value: any) => {
  const raw = String(value || '').trim().toLowerCase().replace('.', '');
  if (!raw || raw === 'child' || raw === 'cnn') return String(titleOptions[0].value);
  const hasOption = titleOptions.some((option) => option.value === raw);
  return hasOption ? raw : String(titleOptions[0].value);
};

const getDefaultAdultDob = (): Date => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 30);
  return date;
};

const getSafeDate = (value?: Date | string): Date => {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime()) ? new Date() : date;
};

const getClampedCxlDateIso = (cxlDate: string | undefined, checkInDate?: Date | string): string => {
  const maxDate = getSafeDate(checkInDate);
  const selectedDate = cxlDate ? new Date(cxlDate) : null;
  if (!selectedDate || Number.isNaN(selectedDate.getTime()) || selectedDate > maxDate) {
    return maxDate.toISOString();
  }
  return selectedDate.toISOString();
};

const getChildDobRange = (
  ageValue: any,
  referenceDate?: Date | string
): { minDate: Date; maxDate: Date; defaultDate: Date } => {
  const age = Math.min(16, Math.max(2, Number(ageValue || 2)));
  const parsedReferenceDate = referenceDate ? new Date(referenceDate) : new Date();
  const baseDate = Number.isNaN(parsedReferenceDate.getTime()) ? new Date() : parsedReferenceDate;
  const maxDate = new Date(baseDate);
  maxDate.setFullYear(maxDate.getFullYear() - age);
  const minDate = new Date(maxDate);
  minDate.setFullYear(minDate.getFullYear() - 1);
  return { minDate, maxDate, defaultDate: maxDate };
};

const emptyRoomClass = { code: '', desc: '', descLoc: '' };

const getRoomOccupancy = (roomTypeCode: string) => {
  const selectedOption = moManualRoomTypeOptions.find((option) => option.value === roomTypeCode) as any;
  const numAdt = Math.max(0, Number(selectedOption?.data?.numAdt || 0));
  const numCnn = Math.max(0, Number(selectedOption?.data?.numCnn || 0));
  const numPax = numAdt + numCnn || 1;
  return { numAdt, numCnn, numPax };
};

const createPaxesLayout = (roomTypeCode: string) => {
  const { numAdt, numCnn } = getRoomOccupancy(roomTypeCode);
  const total = Math.max(1, numAdt + numCnn);

  return Array.from({ length: total }, (_, index) => ({
    isChild: index >= numAdt,
  }));
};

const buildNextPaxes = (
  nextRows: ManualRoomRow[],
  prevPaxes: any[],
  checkInDate?: string,
) => {
  let globalIndex = 0;
  const nextPaxes: any[] = [];

  nextRows.forEach((row, roomIndex) => {
    const paxLayout = createPaxesLayout(row.roomType);
    paxLayout.forEach((layout) => {
      const current = prevPaxes[globalIndex] || {};
      const childRange = layout.isChild ? getChildDobRange(current.manualChildAge, checkInDate) : null;
      const currentDob = current.dob ? new Date(current.dob) : null;
      const isCurrentDobValid = !!currentDob && !Number.isNaN(currentDob.getTime());
      const adultDefaultDobIso = getDefaultAdultDob().toISOString();
      const childDefaultDobIso = childRange?.defaultDate.toISOString() || adultDefaultDobIso;
      const movedFromChildToAdult = !layout.isChild && !!current.manualIsChild;
      const nextDob = layout.isChild
        ? (isCurrentDobValid &&
          currentDob! >= childRange!.minDate &&
          currentDob! <= childRange!.maxDate
          ? current.dob
          : childDefaultDobIso)
        : (movedFromChildToAdult ? adultDefaultDobIso : (isCurrentDobValid ? current.dob : adultDefaultDobIso));
      nextPaxes.push({
        ...current,
        paxCode: globalIndex + 1,
        paxTitle: layout.isChild
          ? childTitleOption.value
          : normalizeTitleValue(current.paxTitle),
        paxTypeCode: layout.isChild ? 'CNN' : current.paxTypeCode || 'ADT',
        firstName: current.firstName || '',
        lastName: current.lastName || '',
        dob: nextDob,
        manualRoomIndex: roomIndex,
        manualIsChild: layout.isChild,
        manualChildAge: layout.isChild ? String(current.manualChildAge || '2') : undefined,
      });
      globalIndex += 1;
    });
  });

  return nextPaxes;
};

const buildManualBookRooms = (
  roomRows: ManualRoomRow[],
  paxes: any[],
  checkInDate?: string,
) => {
  let paxOffset = 0;

  return roomRows.map((row) => {
    const paxCount = createPaxesLayout(row.roomType).length;
    const roomPaxes = paxes.slice(paxOffset, paxOffset + paxCount);
    paxOffset += paxCount;
    const paxCodes = roomPaxes.map((pax) => pax.paxCode);
    const { numAdt, numCnn, numPax } = getRoomOccupancy(row.roomType);

    return {
      delRow: 0,
      sysRoomCode: row.roomType,
      numAdt,
      numCnn,
      numPax,
      sysRoomClass: emptyRoomClass,
      suppRoomClass: emptyRoomClass,
      suppRoom: { code: row.supplierRoomCode, desc: row.basicDesc, descLoc: '' },
      sysBasisCode: row.meal,
      basisCode: row.meal,
      basisDesc: row.basicDesc,
      meal: { code: row.meal, desc: '', descLoc: '' },
      sysStatus: { code: row.status, desc: '', descLoc: '' },
      cancellationDateLimit: getClampedCxlDateIso(row.cxlDate, checkInDate),
      paxCodes,
      paxes: roomPaxes,
      roomPaxString: paxCodes.join(','),
    };
  });
};

const applyManualRoomsToOrder = (
  order: Order,
  roomRows: ManualRoomRow[],
  paxesOverride?: any[],
  checkInDate?: string,
): Order => {
  const segment = order.segments?.[0] || {};
  const nextPaxes = paxesOverride ?? buildNextPaxes(roomRows, (order.paxes as any[]) || [], checkInDate);
  const rooms = buildManualBookRooms(roomRows, nextPaxes, checkInDate);

  return {
    ...order,
    paxes: nextPaxes,
    segments: [{ ...segment, rooms: rooms as Room[], numberOfRooms: rooms.length }],
  };
};

interface MoManualRoomsProps {
  orderModel: Order;
  setOrderModel: Dispatch<SetStateAction<Order>>;
  isSubmitted: boolean;
  orderBuilderRef?: MutableRefObject<(() => Order) | null>;
}

const MoManualRooms: FC<MoManualRoomsProps> = ({ orderModel, setOrderModel, isSubmitted, orderBuilderRef }) => {
  const { t } = useTranslation();
  const checkInDate = (orderModel.segments?.[0] as any)?.checkIn;
  const [roomCount, setRoomCount] = useState('2');
  const [loadingRandomNames, setLoadingRandomNames] = useState(false);
  const [roomType, setRoomType] = useState(String(moManualRoomTypeOptions[0]?.value || ''));
  const [statusOptions, setStatusOptions] = useState<Select[]>([{ value: 'OK', label: '[OK] Ok' }]);
  const [rows, setRows] = useState<ManualRoomRow[]>([
    {
      roomType: '',
      childrenAge: '',
      meal: 'RO',
      status: 'OK',
      supplierRoomCode: '',
      basicDesc: '',
      cxlDate: getClampedCxlDateIso(undefined, checkInDate),
    },
    {
      roomType: '',
      childrenAge: '',
      meal: 'RO',
      status: 'OK',
      supplierRoomCode: '',
      basicDesc: '',
      cxlDate: getClampedCxlDateIso(undefined, checkInDate),
    },
  ]);

  const syncOrderFromRows = (nextRows: ManualRoomRow[]) => {
    setOrderModel((prev) => applyManualRoomsToOrder(prev, nextRows, undefined, checkInDate));
  };

  useEffect(() => {
    (async () => {
      const data = await segmentEditorOther();
      const statusMap = data?.activeStatus || data?.allStatus || {};
      const nextStatusOptions =
        Object.entries(statusMap).map(([code, desc]) => ({
          value: code,
          label: `[${code}] ${desc as string}`,
        })) || [];

      setStatusOptions(nextStatusOptions.length ? nextStatusOptions : [{ value: 'OK', label: '[OK] Ok' }]);
      if (moManualRoomTypeOptions.length) {
        const firstRoomType = String(moManualRoomTypeOptions[0].value);
        setRoomType(firstRoomType);
        setRows((prev) =>
          prev.map((row) => ({
            ...row,
            roomType: row.roomType || firstRoomType,
          }))
        );
      }
    })();
  }, []);

  const rowsCount = useMemo(() => Number(roomCount || '1'), [roomCount]);

  useEffect(() => {
    const defaultStatus = String('OK');
    setRows((prev) =>
      Array.from({ length: rowsCount }, (_, idx) => ({
        roomType: prev[idx]?.roomType || roomType || '',
        childrenAge: prev[idx]?.childrenAge || '',
        meal: prev[idx]?.meal || 'RO',
        status: prev[idx]?.status || defaultStatus,
        supplierRoomCode: prev[idx]?.supplierRoomCode || '',
        basicDesc: prev[idx]?.basicDesc || '',
        cxlDate: getClampedCxlDateIso(prev[idx]?.cxlDate, checkInDate),
      }))
    );
  }, [rowsCount, roomType, statusOptions, checkInDate]);

  useEffect(() => {
    if (!rows.length) return;
    syncOrderFromRows(rows);
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!orderBuilderRef) return;
    orderBuilderRef.current = () => applyManualRoomsToOrder(orderModel, rows, undefined, checkInDate);
  }, [orderBuilderRef, orderModel, rows, checkInDate]);

  const onChangeRow = (index: number, patch: Partial<ManualRoomRow>) => {
    setRows((prev) => prev.map((row, idx) => (idx === index ? { ...row, ...patch } : row)));
  };

  const onChangeOrderPax = (globalPaxIndex: number, patch: Record<string, any>, isChild: boolean) => {
    setOrderModel((prev) => {
      const nextPaxes: any[] = [...((prev.paxes as any[]) || [])];
      const current = nextPaxes[globalPaxIndex] || {};
      nextPaxes[globalPaxIndex] = {
        ...current,
        ...patch,
        paxTitle: isChild
          ? childTitleOption.value
          : normalizeTitleValue(patch.paxTitle || current.paxTitle),
        paxTypeCode: isChild ? 'CNN' : current.paxTypeCode || 'ADT',
      };
      return applyManualRoomsToOrder(prev, rows, nextPaxes, checkInDate);
    });
  };

  const onChangeChildAge = (globalPaxIndex: number, ageValue: string) => {
    const childRange = getChildDobRange(ageValue, checkInDate);
    onChangeOrderPax(
      globalPaxIndex,
      { manualChildAge: String(ageValue || '2'), dob: childRange.defaultDate.toISOString() },
      true
    );
  };

  const onCopyLastName = () => {
    const paxes = (orderModel.paxes as any[]) || [];
    if (!paxes.length) return;
    const source = String(paxes[0]?.lastName ?? '').trim();
    if (!source) return;
    setOrderModel((prev) => {
      const prevPaxes = (prev.paxes as any[]) || [];
      const nextPaxes = prevPaxes.map((p) => {
        const first = String(p?.firstName ?? '').trim();
        const fullName = `${first} ${source}`.trim();
        return {
          ...p,
          lastName: source,
          fullName,
          fullDisplayName: fullName,
        };
      });
      return applyManualRoomsToOrder(prev, rows, nextPaxes, checkInDate);
    });
  };

  const onGetRandomNames = async (emptyOnly = false) => {
    const slots = rows.flatMap((row) => createPaxesLayout(row.roomType));
    if (!slots.length) return;
    const existingPaxes = (orderModel.paxes as any[]) || [];
    const slotsToFill = slots.filter((slot, idx) => {
      if (!emptyOnly) return true;
      const pax = existingPaxes[idx] || {};
      const hasFirstName = !!String(pax?.firstName ?? '').trim();
      const hasLastName = !!String(pax?.lastName ?? '').trim();
      return !hasFirstName || !hasLastName;
    });
    if (!slotsToFill.length) return;
    const maleCount = slotsToFill.filter((s) => !s.isChild).length;
    const femaleCount = slotsToFill.filter((s) => s.isChild).length;
    setLoadingRandomNames(true);
    try {
      const data = await getRandomName(maleCount, femaleCount);
      const maleNames: any[] = data?.maleNames || [];
      const femaleNames: any[] = data?.femaleNames || [];
      if (maleCount > 0 && !maleNames.length) {
        showError(t('something_went_wrong'));
        return;
      }
      if (femaleCount > 0 && !femaleNames.length) {
        showError(t('something_went_wrong'));
        return;
      }
      let mi = 0;
      let fi = 0;
      setOrderModel((prev) => {
        const prevPaxes = (prev.paxes as any[]) || [];
        const nextPaxes = prevPaxes.map((pax, idx) => {
          const slot = slots[idx];
          if (!slot) return pax;
          const hasFirstName = !!String(pax?.firstName ?? '').trim();
          const hasLastName = !!String(pax?.lastName ?? '').trim();
          if (emptyOnly && hasFirstName && hasLastName) return pax;
          const src = slot.isChild
            ? femaleNames[fi++ % femaleNames.length]
            : maleNames[mi++ % maleNames.length];
          const randomFirstName = String(src?.firstName ?? '').trim();
          const randomLastName = String(src?.lastName ?? '').trim();
          const firstName = emptyOnly && hasFirstName ? String(pax?.firstName ?? '') : randomFirstName;
          const lastName = emptyOnly && hasLastName ? String(pax?.lastName ?? '') : randomLastName;
          const fullName = `${firstName} ${lastName}`.trim();
          return {
            ...pax,
            firstName,
            firstDisplayName: firstName,
            lastName,
            fullName,
            fullDisplayName: fullName,
          };
        });
        return applyManualRoomsToOrder(prev, rows, nextPaxes, checkInDate);
      });
    } finally {
      setLoadingRandomNames(false);
    }
  };

  return (
    <div className="mo-block mt-15">
      <div className="mo-block-title">Rooms & Paxes</div>
      <div className="mo-block-content">
        <div className="row mb-10">
          <div className="col-12 col-md-2 mb-10">
            <SelectCustom
              options={roomCountOptions}
              value={roomCount}
              onChange={(value) => setRoomCount(String(value || '1'))}
            />
          </div>
          <div className="col-12 col-md-3 mb-10">
            <SelectCustom
              options={moManualRoomTypeOptions}
              value={roomType}
              onChange={(value) => {
                const next = String(value || '');
                setRoomType(next);
                setRows((prev) =>
                  prev.map((row) => ({
                    ...row,
                    roomType: next,
                  }))
                );
              }}
            />
          </div>
          <div className="col-12 col-md-7 mb-10 flex-row">
            <div className='ml-a'></div>
            <Button
              title="Copy Last Name"
              type="button"
              onClick={onCopyLastName}
              className="btn btn-default mr-10"
              icon={faCopy}
              iconclassname="mr-5"
            />
            <Button
              title="Random Names"
              type="button"
              onClick={() => onGetRandomNames()}
              className="btn btn-primary mr-10"
              icon={faShuffle}
              iconclassname="mr-5"
              disabled={loadingRandomNames}
            />
            <Button
              title="Random Empty Names"
              type="button"
              onClick={() => onGetRandomNames(true)}
              className="btn btn-default"
              icon={faShuffle}
              iconclassname="mr-5"
              disabled={loadingRandomNames}
            />
          </div>
        </div>

        <div className="table-default table-small text-sm" style={{ overflowY: 'visible' }}>
          <table className="table-bordered-lines table-mobile">
            <thead>
              <tr>
                <th>#</th>
                <th>Room Type</th>
                <th>Meal</th>
                <th>Status</th>
                <th>Title</th>
                <th>Last Name</th>
                <th>First Name</th>
                <th>DOB</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const paxLayout = createPaxesLayout(row.roomType);
                const offset = rows
                  .slice(0, index)
                  .reduce((acc, currentRow) => acc + createPaxesLayout(currentRow.roomType).length, 0);
                return <tr key={index}>
                  <td>{index + 1}</td>
                  <td style={{ minWidth: 180 }}>
                    <SelectCustom
                      options={moManualRoomTypeOptions}
                      value={row.roomType}
                      onChange={(value) => {
                        const nextType = String(value || '');
                        onChangeRow(index, {
                          roomType: nextType,
                        });
                      }}
                    />
                    <div className='position-relative mt-5 mb-15' style={{ top: 5 }}>
                      <FormGroup
                        value={row.supplierRoomCode || ''}
                        onChange={(supplierRoomCode) => onChangeRow(index, { supplierRoomCode })}
                        label=""
                        movablePlaceholder='Supplier Room Code'
                        movablePlaceholderSm={true}
                      />
                    </div>
                  </td>
                  <td style={{ minWidth: 180 }}>
                    <SelectCustom
                      options={mealOptions}
                      value={row.meal}
                      onChange={(value) => onChangeRow(index, { meal: String(value || 'RO') })}
                    />
                    <div className='position-relative mt-5' style={{ top: 5 }}>
                      <FormGroup
                        value={row.basicDesc || ''}
                        onChange={(basicDesc) => onChangeRow(index, { basicDesc })}
                        label=""
                        movablePlaceholder='Basic Desc'
                        movablePlaceholderSm={true}
                      />
                    </div>
                  </td>
                  <td style={{ minWidth: 180 }}>
                    <SelectCustom
                      options={statusOptions}
                      value={row.status}
                      onChange={(value) => onChangeRow(index, { status: String(value || 'OK') })}
                    />
                    <div className='position-relative' style={{ top: 10 }}>
                      <FormGroupDatepicker
                        value={getSafeDate(row.cxlDate || checkInDate)}
                        onChange={(cxlDate) =>
                          onChangeRow(index, { cxlDate: getClampedCxlDateIso(cxlDate.toISOString(), checkInDate) })
                        }
                        maxDate={getSafeDate(checkInDate)}
                        label=""
                        movablePlaceholder="CXL Date"
                        movablePlaceholderSm={true}
                      />
                    </div>
                  </td>
                  <td style={{ minWidth: 120 }}>
                    {paxLayout.map((layoutPax, paxIndex) => {
                      const globalPaxIndex = offset + paxIndex;
                      const pax: any = (orderModel.paxes as any[])?.[globalPaxIndex] || {};
                      const isChild = !!layoutPax.isChild;
                      return (
                        <div key={`${index}-${paxIndex}-title`} className={paxIndex < paxLayout.length - 1 ? 'mb-10' : ''}>
                          <div className='position-relative'>
                            <SelectCustom
                              options={isChild ? [childTitleOption] : titleOptions}
                              value={isChild ? childTitleOption.value : normalizeTitleValue(pax.paxTitle)}
                              onChange={(value) =>
                                onChangeOrderPax(
                                  globalPaxIndex,
                                  { paxTitle: normalizeTitleValue(value) },
                                  isChild
                                )
                              }
                              disabled={isChild}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </td>
                  <td style={{ minWidth: 180 }}>
                    {paxLayout.map((layoutPax, paxIndex) => {
                      const globalPaxIndex = offset + paxIndex;
                      const pax: any = (orderModel.paxes as any[])?.[globalPaxIndex] || {};
                      const isChild = !!layoutPax.isChild;
                      return (
                        <div key={`${index}-${paxIndex}-last`} className={paxIndex < paxLayout.length - 1 ? 'mb-10' : ''}>
                          <div className='position-relative' style={{ top: -3 }}>
                            <FormGroup
                              value={pax.lastName || ''}
                              onChange={(lastName) => onChangeOrderPax(globalPaxIndex, { lastName }, isChild)}
                              label=""
                              isSubmitted={isSubmitted}
                              validation={{ required: true }}
                              movablePlaceholder=' '
                              movablePlaceholderSm={true}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </td>
                  <td style={{ minWidth: 180 }}>
                    {paxLayout.map((layoutPax, paxIndex) => {
                      const globalPaxIndex = offset + paxIndex;
                      const pax: any = (orderModel.paxes as any[])?.[globalPaxIndex] || {};
                      const isChild = !!layoutPax.isChild;
                      return (
                        <div key={`${index}-${paxIndex}-first`} className={paxIndex < paxLayout.length - 1 ? 'mb-10' : ''}>
                          <div className='position-relative' style={{ top: -3 }}>
                            <FormGroup
                              value={pax.firstName || ''}
                              onChange={(firstName) => onChangeOrderPax(globalPaxIndex, { firstName }, isChild)}
                              label=""
                              isSubmitted={isSubmitted}
                              validation={{ required: true }}
                              movablePlaceholder=' '
                              movablePlaceholderSm={true}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </td>
                  <td style={{ minWidth: 220 }}>
                    {paxLayout.map((layoutPax, paxIndex) => {
                      const globalPaxIndex = offset + paxIndex;
                      const pax: any = (orderModel.paxes as any[])?.[globalPaxIndex] || {};
                      const isChild = !!layoutPax.isChild;
                      const childAge = String(pax?.manualChildAge || '2');
                      const childRange = isChild ? getChildDobRange(childAge, checkInDate) : null;
                      const fallbackDob = isChild ? childRange!.defaultDate : getDefaultAdultDob();
                      const parsedDob = pax?.dob ? new Date(pax.dob) : fallbackDob;
                      const dobValue = Number.isNaN(parsedDob.getTime()) ? fallbackDob : parsedDob;
                      return (
                        <div key={`${index}-${paxIndex}-dob`} style={{ marginBottom: -10 }}>
                          <div className='form-group-datepicker-right'>
                            <div className='flex-between'>
                              {isChild && (
                                <Tooltip content='Child age calculated by checkin date' position='top' >
                                  <div style={{ width: 40, paddingTop: 5 }}>
                                    <SelectCustom
                                      options={childAgeOptions}
                                      value={childAge}
                                      onChange={(value) => onChangeChildAge(globalPaxIndex, String(value || '2'))}
                                    />
                                  </div>
                                </Tooltip>
                              )}
                              <div className='position-relative' style={isChild ? { width: 'calc(100% - 50px)' } : { width: '100%' }}>
                                <FormGroupDatepicker
                                  value={dobValue}
                                  onChange={(dob) => onChangeOrderPax(globalPaxIndex, { dob: dob.toISOString() }, isChild)}
                                  minDate={isChild ? childRange!.minDate : undefined}
                                  maxDate={isChild ? childRange!.maxDate : undefined}
                                  label=""
                                  movablePlaceholder=""
                                  movablePlaceholderSm={true}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(MoManualRooms, 'MoManualRooms');
